import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from "@mui/material";

import { alcanceDoConvite } from "./alcance";
import { NOME_DO_PAPEL, TEXTOS } from "./frases";
import { ErroDeAcesso } from "./gestao-de-usuarios.service";
import { IClienteDaCarteira, IUsuarioDaOrganizacao, Lado, PedidoDeAlcance } from "./gestao-de-usuarios.types";
import { ModalDeAlcance } from "./ModalDeAlcance";

// why: Consulta é implícita (todo vínculo a tem); a tela escolhe só os papéis que se somam.
export const PAPEIS_ESCOLHIVEIS = ["OPERACAO", "CONFIGURACAO", "GESTAO"] as const;
export const codigoDoPapel = (lado: Lado, papel: string) => `${lado}_${papel}`;

function CaixasDePapeis({ marcados, onChange }: { marcados: string[]; onChange: (p: string[]) => void }) {
  const alternar = (p: string) => onChange(marcados.includes(p) ? marcados.filter((x) => x !== p) : [...marcados, p]);
  return (
    <FormGroup>
      <FormControlLabel control={<Checkbox checked disabled />} label={NOME_DO_PAPEL.CONSULTA} />
      {PAPEIS_ESCOLHIVEIS.map((p) => (
        <FormControlLabel
          key={p}
          control={<Checkbox checked={marcados.includes(p)} onChange={() => alternar(p)} />}
          label={NOME_DO_PAPEL[p]}
        />
      ))}
      <Typography variant="caption">{TEXTOS.CONSULTA_INCLUIDA}</Typography>
    </FormGroup>
  );
}

interface IModalBase {
  aberto: boolean;
  onFechar: () => void;
  ocupado: boolean;
}

export function ModalDeConvite({
  aberto,
  onFechar,
  ocupado,
  organizationId,
  podeAlcance,
  carteira,
  onConvidar,
}: IModalBase & {
  organizationId: string;
  /** Corretora, Gestão com toda a carteira: a rota aceitaria o alcance (podeConcederAlcance). */
  podeAlcance: boolean;
  /** Carrega sob demanda, ao clicar em Mudar. */
  carteira: () => Promise<IClienteDaCarteira[]>;
  onConvidar: (c: { nome: string; email: string; papeis: string[]; alcance?: PedidoDeAlcance }) => void;
}) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [papeis, setPapeis] = useState<string[]>([]);
  // why: sem pedido = toda a carteira (padrão do backend); a chave alcance só vai quando mudou para SELECAO.
  const [selecao, setSelecao] = useState<string[]>();
  const [lida, setLida] = useState<IClienteDaCarteira[]>();
  const [erro, setErro] = useState<string>();
  async function mudar() {
    setErro(undefined);
    try {
      setLida(await carteira());
    } catch (e) {
      setErro(e instanceof ErroDeAcesso ? e.message : TEXTOS.ERRO_AO_CARREGAR);
    }
  }
  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="xs">
      <DialogTitle>Convidar</DialogTitle>
      <DialogContent>
        <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} fullWidth margin="dense" />
        <TextField
          label="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          margin="dense"
        />
        <CaixasDePapeis marcados={papeis} onChange={setPapeis} />
        {podeAlcance && (
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">
              {`${TEXTOS.ALCANCE}: ${selecao ? TEXTOS.N_EMPRESAS(selecao.length) : TEXTOS.TODA_A_CARTEIRA}`}
            </Typography>
            <Button size="small" onClick={mudar}>
              {TEXTOS.MUDAR}
            </Button>
          </Box>
        )}
        {erro && (
          <Typography variant="caption" color="error">
            {erro}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={ocupado || !nome.trim() || !email.trim()}
          onClick={() =>
            onConvidar({
              nome: nome.trim(),
              email: email.trim(),
              papeis,
              ...(selecao && { alcance: { alcance: "SELECAO", organizacoes: selecao } }),
            })
          }
        >
          Convidar
        </Button>
      </DialogActions>
      {lida && (
        <ModalDeAlcance
          aberto
          ocupado={false}
          usuario={{ nome: nome.trim() || "quem for convidado" }}
          atual={alcanceDoConvite(
            { alcance: selecao ? "SELECAO" : "ORGANIZACAO", selecao: selecao ?? [] },
            lida,
            organizationId,
          )}
          carteira={lida}
          onFechar={() => setLida(undefined)}
          onSalvar={(p) => {
            setSelecao(p.alcance === "SELECAO" ? p.organizacoes : undefined);
            setLida(undefined);
          }}
        />
      )}
    </Dialog>
  );
}

export function ModalDePapeis({
  aberto,
  onFechar,
  ocupado,
  usuario,
  lado,
  onSalvar,
}: IModalBase & {
  /** Pessoa da lista ou convite pendente: só o nome e os papéis entram aqui. */
  usuario: Pick<IUsuarioDaOrganizacao, "nome" | "papeis">;
  lado: Lado;
  onSalvar: (mudancas: { dar: string[]; tirar: string[] }) => void;
}) {
  const atuais = PAPEIS_ESCOLHIVEIS.filter((p) => usuario.papeis.includes(codigoDoPapel(lado, p)));
  const [marcados, setMarcados] = useState<string[]>(atuais);
  const dar = marcados.filter((p) => !atuais.includes(p as (typeof atuais)[number]));
  const tirar = atuais.filter((p) => !marcados.includes(p));
  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="xs">
      <DialogTitle>{`Papéis de ${usuario.nome}`}</DialogTitle>
      <DialogContent>
        <CaixasDePapeis marcados={marcados} onChange={setMarcados} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={ocupado || (dar.length === 0 && tirar.length === 0)}
          onClick={() =>
            onSalvar({ dar: dar.map((p) => codigoDoPapel(lado, p)), tirar: tirar.map((p) => codigoDoPapel(lado, p)) })
          }
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ModalDeRevisao({
  aberto,
  onFechar,
  ocupado,
  usuarios,
  lado,
  onConfirmar,
}: IModalBase & { usuarios: IUsuarioDaOrganizacao[]; lado: Lado; onConfirmar: (gestores: string[]) => void }) {
  // why: na empresa, staff nunca é Gestão própria (§3.4); a revisão recusaria.
  const elegiveis = usuarios.filter((u) => !u.suspenso && (lado !== "EMPRESA" || !u.ehStaff));
  const [marcados, setMarcados] = useState<string[]>(
    elegiveis.filter((u) => u.papeis.includes(codigoDoPapel(lado, "GESTAO"))).map((u) => u.organizationUserId),
  );
  const alternar = (id: string) =>
    setMarcados(marcados.includes(id) ? marcados.filter((x) => x !== id) : [...marcados, id]);
  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Confirmar administradores</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {TEXTOS.REVISAO_EXPLICA}
        </Typography>
        {lado === "EMPRESA" && (
          <Typography variant="body2" gutterBottom>
            {TEXTOS.REVISAO_EMPRESA}
          </Typography>
        )}
        <FormGroup>
          {elegiveis.map((u) => (
            <FormControlLabel
              key={u.organizationUserId}
              control={
                <Checkbox
                  checked={marcados.includes(u.organizationUserId)}
                  onChange={() => alternar(u.organizationUserId)}
                />
              }
              label={`${u.nome} (${u.email}): administra os acessos`}
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={ocupado || (lado !== "EMPRESA" && marcados.length === 0)}
          onClick={() => onConfirmar(marcados)}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ModalDeConfirmacao({
  aberto,
  onFechar,
  ocupado,
  titulo,
  texto,
  acao,
  desistir = "Cancelar",
  onConfirmar,
}: IModalBase & { titulo: string; texto: string; acao: string; desistir?: string; onConfirmar: () => void }) {
  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="xs">
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{texto}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>{desistir}</Button>
        <Button variant="contained" color="error" disabled={ocupado} onClick={onConfirmar}>
          {acao}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
